import { z } from 'zod'

// Base API Response Schema
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.any().nullable()
})

// Paginated Response Schema
export const PaginatedResponseSchema = z.object({
  totalPage: z.number(),
  totalElements: z.number(),
  totalOfAllElements: z.number(),
  data: z.array(z.any())
})

// Pagination Params Schema
export const PaginationParamsSchema = z.object({
  page: z.number().min(1).default(1),
  size: z.number().min(1).max(100).default(10)
})

// Press Release Schema (for list/filter responses)
export const PressReleaseSchema = z.object({
  pressReleaseId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string().optional(),
  articleTag: z.array(z.string()).default([]),
  landingPage: z.boolean().optional(),
  pressRelease: z.boolean().optional(),
  pressReleaseBanner: z.boolean().optional(),
  allowComment: z.boolean().optional(),
  content: z.string(),
  totalViews: z.number().optional(),
  category: z.string().optional(),
  summary: z.string().optional(),
  subtitle: z.string().optional(),
  readTimeMinutes: z.number().optional(),
  updatedAt: z.string().optional(), 
  landingPagePosition: z.number().optional(), // New field for landing page position
})

// Press Release Detail Schema (for get-by-ID responses)
export const PressReleaseDetailSchema = z.object({
  pressReleaseId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string().optional(),
  articleTag: z.array(z.string()).default([]),
  landingPage: z.boolean().optional(),
  pressRelease: z.boolean().optional(),
  pressReleaseBanner: z.boolean().optional(),
  allowComment: z.boolean().optional(),
  content: z.string(),
  totalViews: z.number().optional(),
    additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional()
})

// Press Release Filter Params Schema
export const PressReleaseFilterParamsSchema = z.object({
  landingPage: z.boolean().optional(),
  pressRelease: z.boolean().optional(),
  pressReleaseBanner: z.boolean().optional(),
  allowComment: z.boolean().optional(),
})

// Type exports
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ApiResponse<T = any> = {
  success: boolean
  message: string
  data: T | null
}

export type PaginatedResponse<T> = {
  totalPage: number
  totalElements: number
  totalOfAllElements: number
  data: T[]
}

export type PaginationParams = z.infer<typeof PaginationParamsSchema>
export type PressRelease = z.infer<typeof PressReleaseSchema>
export type PressReleaseDetail = z.infer<typeof PressReleaseDetailSchema>
export type PressReleaseFilterParams = z.infer<typeof PressReleaseFilterParamsSchema>

// Programme Schema (for list/filter responses)
export const ProgrammeSchema = z.object({
  programmeId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByUsername: z.string(),
  title: z.string(),
  coverImageUrl: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.string(),
  landingPageAfsaProgramme: z.boolean(),
  allowComments: z.boolean(),
  content: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const MicroSiteSchema = z.object({
  micrositeTemplateType: z.string(),
  branding: z.object({
    websiteLogoUrl: z.url(),
    websiteColorCode: z.string().optional(),
    textColorCode: z.string().optional()
  }).optional(),
  headerSection: z.object({
    headerImageUrl: z.preprocess(
      (val) => (val === "" ? null : val),
      z.string().url().nullable().optional()
    ),
    headerTitle: z.string().optional(),
    headerDescription: z.string().optional()
  }).optional(),
  headerSectionTwo: z.object({
    headerSectionTwoTitle: z.string().optional(),
    headerSectionTwoDescription: z.string().optional()
  }).optional(),
  missionTimeLineSection: z.object({
    missionTimeLineTitle: z.string().optional(),
    missionTimeLineDescription: z.string().optional(),
    missionTimeLineSectionDetails: z.array(z.object({
      missionTimeLineStartDate: z.string().optional(),
      missionTimeLineEndDate: z.string().optional(),
      missionTimeLineSectionTitle: z.string().optional(),
      missionTimeLineSectionDescription: z.string().optional(),
      missionTimeLineSectionImageUrl: z.preprocess(
        (val) => (val === "" ? null : val),
        z.string().url().nullable().optional()
      )
    })).optional().nullable()
  }).optional(),
  newsFeedSection: z.object({
    newsFeedTitle: z.string().optional(),
    newsFeedDescription: z.string().optional(),
    newsFeedSectionDetails: z.array(z.object({
      newsFeedSectionTitle: z.string().optional(),
      newsFeedSectionDescription: z.string().optional(),
      newsFeedSectionLink: z.preprocess(
        (val) => (val === "" ? null : val),
        z.string().url().nullable().optional()
      ),
      newsFeedSectionImageUrl: z.preprocess(
        (val) => (val === "" ? null : val),
        z.string().url().nullable().optional()
      )
    })).optional()
  }).optional(),
  footerSection: z.object({
    footerEmail: z.string().email().optional(),
    footerAddress: z.string().optional(),
    footerDescription: z.string().optional(),
    socialOne: z.string().url().optional(),
    socialTwo: z.string().url().optional(),
    socialThree: z.string().url().optional(),
    socialLinks: z.object({
      facebook: z.string().optional(),
      twitter: z.string().optional(),
      linkedIn: z.string().optional(),
      instagram: z.string().optional(),
      youtube: z.string().optional()
    }).optional()
  }).optional()
}).nullable()

// Programme Detail Schema (for get by ID responses)
export const ProgrammeDetailSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  title: z.string(),
  coverImageUrl: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.array(z.string()).default([]),
  landingPageAfsaProgramme: z.boolean(),
  allowComments: z.boolean(),
  content: z.string(),
  status: z.string(),
  datePublished: z.string().nullable(),
  isAddWebsite: z.boolean().optional(),
  websiteType: z.string().optional(),
  websiteLink: z.string().optional(),
  microSite: MicroSiteSchema.nullable()
})

// Programme Filter Params Schema
export const ProgrammeFilterParamsSchema = z.object({
  title: z.string().optional(),
  articleTag: z.string().optional(),
  landingPageAfsaProgramme: z.boolean().optional(),
  allowComments: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  year: z.string().optional(),
  month: z.string().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

// Strategic Pillar Schema (for list/filter responses)
export const StrategicPillarSchema = z.object({
  strategicPillarId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string().optional(),
  articleTag: z.array(z.string()).default([]),
  landingPagePrioritiesOfAfsa: z.boolean().optional(),
  whoWeAre: z.boolean().optional(),
  countries: z.boolean().optional(),
  history: z.boolean().optional(),
  member: z.boolean().optional(),
  content: z.string(),
  totalViews: z.number().optional()
})

// Strategic Pillar Detail Schema (for get by ID responses)
export const StrategicPillarDetailSchema = z.object({
  strategicPillarId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string().optional(),
  articleTag: z.array(z.string()).default([]),
  landingPagePrioritiesOfAfsa: z.boolean().optional(),
  whoWeAre: z.boolean().optional(),
  countries: z.boolean().optional(),
  history: z.boolean().optional(),
  member: z.boolean().optional(),
  content: z.string(),
  totalViews: z.number().optional()
})

// Strategic Pillar Filter Params Schema
export const StrategicPillarFilterParamsSchema = z.object({
  landingPagePrioritiesOfAfsa: z.boolean().optional(),
  whoWeAre: z.boolean().optional(),
  countries: z.boolean().optional(),
  memberCountries: z.boolean().optional(),
  history: z.boolean().optional(),
  leadership: z.boolean().optional(),
  member: z.boolean().optional(),
  year: z.string().optional(),
  month: z.string().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

// Type exports
export type Programme = z.infer<typeof ProgrammeSchema>
export type ProgrammeDetail = z.infer<typeof ProgrammeDetailSchema>
export type ProgrammeFilterParams = z.infer<typeof ProgrammeFilterParamsSchema>
export type MicroSite = z.infer<typeof MicroSiteSchema>

export type StrategicPillar = z.infer<typeof StrategicPillarSchema>
export type StrategicPillarDetail = z.infer<typeof StrategicPillarDetailSchema>
export type StrategicPillarFilterParams = z.infer<typeof StrategicPillarFilterParamsSchema>

// Comment Schema
export const CommentSchema = z.object({
  id: z.string(),
  pressReleaseId: z.string(),
  name: z.string(),
  email: z.string(),
  comment: z.string(),
  dateCommented: z.string(),
  replyCount: z.number().optional().default(0)
})

// Comment Create Schema
export const CommentCreateSchema = z.object({
  name: z.string(),
  email: z.string().email(),
  comment: z.string()
})

// Comment type exports
export type Comment = z.infer<typeof CommentSchema>
export type CommentCreate = z.infer<typeof CommentCreateSchema>

// Press Release Reply Schema
export const PressReleaseReplySchema = z.object({
  id: z.string(),
  commentId: z.string(),
  name: z.string(),
  email: z.string(),
  reply: z.string(),
  dateReplied: z.string()
})

// Press Release Reply Create Schema
export const PressReleaseReplyCreateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Valid email is required'),
  reply: z.string().min(1, 'Reply is required')
})

// Press Release Reply type exports
export type PressReleaseReply = z.infer<typeof PressReleaseReplySchema>
export type PressReleaseReplyCreate = z.infer<typeof PressReleaseReplyCreateSchema>

// Publication Schema (for list/filter responses)
export const PublicationSchema = z.object({
  publicationId: z.string(),
  publicationUrl: z.string(),
  coverImageUrl: z.string().nullable(),
  title: z.string(),
  description: z.string(),
  publicationCategory: z.string(),
  legalFoundation: z.boolean().optional(),
  homePageKeyResources: z.boolean().nullable().optional(),
  externalLink: z.string().nullable().optional(),
  isExternalSource: z.boolean().nullable().optional(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  totalDownloads: z.number().optional(),
  totalViews: z.number().optional()
})

// Publication Detail Schema (for get by ID responses)
export const PublicationDetailSchema = z.object({
  publicationId: z.string(),
  publicationUrl: z.string(),
  coverImageUrl: z.string().nullable(),
  title: z.string(),
  description: z.string(),
  publicationCategory: z.string(),
  legalFoundation: z.boolean().optional(),
  homePageKeyResources: z.boolean().nullable().optional(),
  externalLink: z.string().nullable().optional(),
  isExternalSource: z.boolean().nullable().optional(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  totalDownloads: z.number().optional(),
  totalViews: z.number().optional()
})

// Publication Filter Params Schema

export const PublicationFilterParamsSchema = z.object({
  publicationCategory: z.string().optional(),
  homePageKeyResources: z.boolean().nullable().optional(),
  year: z.number().optional(),
  month: z.number().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  publishedStartDate: z.string().optional(),
  publishedEndDate: z.string().optional()
})

// Publication type exports
export type Publication = z.infer<typeof PublicationSchema>
export type PublicationDetail = z.infer<typeof PublicationDetailSchema>
export type PublicationFilterParams = z.infer<typeof PublicationFilterParamsSchema>

// Legal Foundation Schema (for public all responses)
export const LegalFoundationSchema = z.object({
  legalFoundationId: z.string(),
  legalFoundationUrl: z.string(),
  coverImageUrl: z.string().nullable().optional(),
  title: z.string(),
  description: z.string(),
  legalFoundationCategory: z.string(),
  homePageKeyResources: z.boolean().nullable().optional(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  totalDownloads: z.number().optional(),
  totalViews: z.number().optional(),
  isExternalSource: z.boolean().nullable().optional()
})

// Legal Foundation Filter Params Schema
export const LegalFoundationFilterParamsSchema = z.object({
  legalFoundationCategory: z.string().optional(),
  homePageKeyResources: z.boolean().nullable().optional(),
  year: z.number().optional(),
  month: z.number().optional()
})

export type LegalFoundationFilterParams = z.infer<typeof LegalFoundationFilterParamsSchema>
export type LegalFoundation = z.infer<typeof LegalFoundationSchema>

// Announcement Schema (for public highlighted responses)
export const AnnouncementHighlightSchema = z.object({
  announcementId: z.string(),
  imageUrl: z.string(),
  title: z.string(),
  description: z.string(),
  contentCategory: z.string(),
  highlighted: z.boolean(),
  dateHighlighted: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string(),
  createdByUsername: z.string(),
  linkedContentId: z.string().nullable(),
  linkedContent: z.object({
    contentId: z.string(),
    imageUrl: z.string(),
    title: z.string(),
    content: z.string(),
  }).nullable()
})

export type AnnouncementHighlight = z.infer<typeof AnnouncementHighlightSchema>


// Procurement Schema (for list/filter responses)

// createdAt
// : 
// "2025-12-09T21:29:05"
// datePublished
// : 
// "2025-12-09T21:29:05.632"
// documentCategory
// : 
// "PROCUREMENT_MANUAL"
// documentId
// : 
// "69388691e7ad3ecd07144904"
// documentUrl
// : 
// "https://res.cloudinary.com/gozade/image/upload/v1765312112/image/image_68af3eeee31e3d9345a2cc5b/image/de23t411fgknxxf7nz6k.jpg"
// title
// : 
// "Dcoument 010"
// totalDownloads
// : 
// 1
// updatedAt
// : 
// "2025-12-09T21:29:05"
export const ProcurementSchema = z.object({
  documentId: z.string(),
  documentUrl: z.string(),
  title: z.string(),
  documentCategory: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  datePublished: z.string().nullable(),
  totalDownloads: z.number().optional()
})

// Procurement Detail Schema (for get by ID responses)
export const ProcurementDetailSchema = z.object({
  documentId: z.string(),
  documentUrl: z.string(),
  title: z.string(),
  documentCategory: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  datePublished: z.string().nullable(),
  totalDownloads: z.number().optional(),
})

// Procurement Filter Params Schema
export const ProcurementFilterParamsSchema = z.object({
  documentCategory: z.string().optional(),
  year: z.number().optional(),
  month: z.number().optional()
})

// Procurement type exports
export type Procurement = z.infer<typeof ProcurementSchema>
export type ProcurementDetail = z.infer<typeof ProcurementDetailSchema>
export type ProcurementFilterParams = z.infer<typeof ProcurementFilterParamsSchema>


// Website Page Section Schema
export const WebPageSectionSchema = z.object({
  sectionTitle: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  contentType: z.string().nullable(),
  fileUrl: z.string().nullable(),
  emailAddress: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  phoneCountryCode: z.string().nullable(),
  address: z.string().nullable(),
  listOfPurpose: z.array(z.string()).nullable(),
  imageUrl: z.string().nullable(),
  sectionTag: z.string(),
})

// Website Page Schema
export const WebPageSchema = z.object({
  websitePageId: z.string(),
  codeName: z.string().optional(), // Marking as optional as it might be deprecated
  websitePageName: z.string().optional(), // New field
  name: z.string().optional(), // Keep for backward compatibility if /all still returns it
  versionNumber: z.string().optional(), // New field
  status: z.string().optional(), // New field
  publishedAt: z.string().optional(), // New field
  publishedBy: z.string().optional(), // New field
  publishedByName: z.string().optional(), // New field
  content: z.record(z.string(), WebPageSectionSchema),
})



// Website Page type export
export type WebPage = z.infer<typeof WebPageSchema>
export type WebPageSection = z.infer<typeof WebPageSectionSchema>
// Gallery Folder Schema
export const GalleryFolderSchema = z.object({
  folderId: z.string(),
  folderName: z.string(),
  previewUrls: z.array(z.string()).optional() // New field for preview image URLs
})

// Photo Detail Schema
export const PhotoDetailSchema = z.object({
  comment: z.string(),
  photoUrl: z.string()
})

// Photo Gallery Title Response Schema (individual gallery in titleResponseList)
export const PhotoGalleryTitleResponseSchema = z.object({
  photoGalleryId: z.string(),
  title: z.string(),
  dateCreated: z.string(),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.string(),
  photoUrl: z.string().nullable().optional(),
  datePublished: z.string().nullable(),
  publishedBy: z.string().nullable(),
  publishedByName: z.string()
})

// Gallery Folder Response Schema (folder with galleries grouped)
export const GalleryFolderResponseSchema = z.object({
  folderId: z.string(),
  folderName: z.string(),
  titleResponseList: z.array(PhotoGalleryTitleResponseSchema)
})

// Photo Gallery Schema (for backward compatibility)
export const PhotoGallerySchema = z.object({
  galleryId: z.string().optional().or(z.literal("")), // keep for backward compatibility if API returns galleryId
  photoGalleryId: z.string(),
  folderId: z.string(),
  folderName: z.string(),
  title: z.string(),
  photoDetails: z.array(PhotoDetailSchema).nullable().optional(),
  photoUrl: z.string().nullable().optional(), // Added photoUrl
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.string().optional(),
  datePublished: z.string().nullable(),
  createdAt: z.string()
})

// Gallery Filter Params Schema
export const GalleryFilterParamsSchema = z.object({
  folderId: z.string().optional(),
  status: z.string().optional(),
  startDate: z.string().optional(),
  isKeyHighlight: z.union([z.boolean(), z.string()]).optional(),
  isMultimedia: z.union([z.boolean(), z.string()]).optional(),
  year: z.string().optional(),
  month: z.string().optional()
})

// Public Gallery Item Schema (for /galleries/public/filter endpoint)
export const PublicGalleryItemSchema = z.object({
  photoGalleryId: z.string(),
  folderId: z.string(),
  folderName: z.string(),
  title: z.string(),
  photoUrl: z.string().nullable(),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  datePublished: z.string().nullable(),
  createdAt: z.string()
})

// Public Gallery Folder Search Result Schema
export const GalleryFolderSearchResultSchema = z.object({
  folderId: z.string(),
  folderName: z.string(),
  previewUrls: z.array(z.string()).optional() 
})

// Gallery type exports
export type PhotoDetail = z.infer<typeof PhotoDetailSchema>
export type GalleryFolder = z.infer<typeof GalleryFolderSchema>
export type PhotoGalleryTitleResponse = z.infer<typeof PhotoGalleryTitleResponseSchema>
export type GalleryFolderResponse = z.infer<typeof GalleryFolderResponseSchema>
export type PhotoGallery = z.infer<typeof PhotoGallerySchema>
export type GalleryFilterParams = z.infer<typeof GalleryFilterParamsSchema>
export type PublicGalleryItem = z.infer<typeof PublicGalleryItemSchema>
export type GalleryFolderSearchResult = z.infer<typeof GalleryFolderSearchResultSchema>

// History Schema (for list/filter responses)
export const HistorySchema = z.object({
  historyId: z.string(),
  imageUrl: z.string(),
  year: z.number(),
  title: z.string(),
  description: z.string(),
  datePublished: z.string().nullable(),
  createdAt: z.string()
})

// History Detail Schema (for get by ID responses)
export const HistoryDetailSchema = z.object({
  historyId: z.string(),
  imageUrl: z.string(),
  year: z.number(),
  title: z.string(),
  description: z.string(),
  datePublished: z.string().nullable(),
  createdAt: z.string()
})

// History Filter Params Schema
export const HistoryFilterParamsSchema = z.object({
  year: z.string().optional()
})

// History type exports
export type History = z.infer<typeof HistorySchema>
export type HistoryDetail = z.infer<typeof HistoryDetailSchema>
export type HistoryFilterParams = z.infer<typeof HistoryFilterParamsSchema>

// Member Schema (for list/filter responses)
export const MemberSchema = z.object({
  memberId: z.string(),
  fullName: z.string(),
  countryId: z.string(),
  countryName: z.string(),
  flagUrl: z.string(),
  memberCategory: z.enum(['PRESIDENT', 'VICE_PRESIDENT', 'MEMBER']),
  imageUrl: z.string(),
  bio: z.string().nullable(),
  linkedInUrl: z.string().nullable(),
  wikipediaUrl: z.string().nullable(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  createdAt: z.string(),
  socialMediaLinks: z.array(z.string()).optional() // New field for social media links
})

// Member Detail Schema (for get by ID responses)
export const MemberDetailSchema = z.object({
  memberId: z.string(),
  fullName: z.string(),
  countryId: z.string(),
  countryName: z.string(),
  flagUrl: z.string(),
  memberCategory: z.enum(['PRESIDENT', 'VICE_PRESIDENT', 'MEMBER']),
  imageUrl: z.string(),
  bio: z.string().nullable(),
  socialMediaLinks: z.array(z.string()).optional(), // New field for social media links
  linkedInUrl: z.string().nullable(),
  wikipediaUrl: z.string().nullable(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  createdAt: z.string()
})

// Member Filter Params Schema
export const MemberFilterParamsSchema = z.object({
  countryId: z.string().optional(),
  memberCategory: z.enum(['PRESIDENT', 'VICE_PRESIDENT', 'MEMBER']).optional()
})

// Member type exports
export type Member = z.infer<typeof MemberSchema>
export type MemberDetail = z.infer<typeof MemberDetailSchema>
export type MemberFilterParams = z.infer<typeof MemberFilterParamsSchema>

// Member Country Schema (for list/filter responses from /countries/public/filter)
export const MemberCountrySchema = z.object({
  countryId: z.string(),
  countryName: z.string(),
  abbreviation: z.string(),
  flagUrl: z.string(),
  description: z.string().nullable(),
  title: z.string(),

})

// Member Country Filter Params Schema
export const MemberCountryFilterParamsSchema = z.object({
  regionTag: z.string().optional()
})

// Member Country type exports
export type MemberCountry = z.infer<typeof MemberCountrySchema>
export type MemberCountryFilterParams = z.infer<typeof MemberCountryFilterParamsSchema>

// Region Schema
export const RegionSchema = z.object({
  regionId: z.string(),
  regionTag: z.string(),
  regionName: z.string(),
  regionDescription: z.string(),
  status: z.string(),
  updatedAt: z.string()
})

// Region type exports
export type Region = z.infer<typeof RegionSchema>

// Job Schema (for list/filter responses)
export const JobSchema = z.object({
  jobId: z.string(),
  jobTitle: z.string(),
  jobLocation: z.string(),
  jobType: z.string(), // e.g., "FULL_TIME", "PER_TIME"
  startDate: z.string(),
  endDate: z.string(),
  description: z.string().nullable(),
  submissionEmail: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  isExpired: z.boolean().nullable(),
  publishedBy: z.string().nullable(),
  publishedByName: z.string(),
  createdBy: z.string().nullable(),
  createdByName: z.string(),
  totalViews: z.number().nullable()
})

// Job Filter Params Schema
export const JobFilterParamsSchema = z.object({
  jobType: z.string().optional(),
  jobLocation: z.string().optional(),
  searchBy: z.string().optional(),
  isExpired: z.boolean().optional()
})

// Job type exports
export type Job = z.infer<typeof JobSchema>
export type JobFilterParams = z.infer<typeof JobFilterParamsSchema>


// Job Application Schema (for list/filter responses)
export const JobApplicationSchema = z.object({
  jobId: z.string(),
  cvUrl: z.string().url().min(1, 'CV is required'),
  coverLetterUrl: z.string().url().optional(),
  education: z.array(z.object({
    schoolName: z.string(),
    degree: z.string(),
    discipline: z.string(),
    optionGrade: z.string().optional(),
    startYear: z.string().min(1, 'Start year is required'),
    endYear: z.string().optional(),
    currentlyStudying: z.boolean().optional(),
    location: z.string().optional(),
    description: z.string().optional()
  })).min(1, 'At least one education entry is required'),
  workExperience: z.array(z.object({
    companyName: z.string(),
    role: z.string(),
    startYear: z.string().min(1, 'Start year is required'),
    endYear: z.string().optional(),
    currentlyWorking: z.boolean().optional(),
    location: z.string().optional(),
    description: z.string().optional(),
    responsibilities: z.array(z.string()).optional(),
    achievements: z.array(z.string()).optional()
  })).optional(),
  email: z.string().email('Valid email is required').min(1, 'Email is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  country: z.string().min(1, 'Country is required'),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  linkedIn: z.string().url().optional(),
  portfolio: z.string().url().optional()
})

// Job Application type exports
export type JobApplication = z.infer<typeof JobApplicationSchema>

// Event Schema (for list/filter responses)
export const EventSchema = z.object({
  eventId: z.string(),
  imageUrl: z.string(),
  eventTitle: z.string(),
  eventVenue: z.string(),
  dateTime: z.string(),
  description: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string().optional()
})

// Event Detail Schema (for get by ID responses)
export const EventDetailSchema = z.object({
  eventId: z.string(),
  imageUrl: z.string(),
  eventTitle: z.string(),
  eventVenue: z.string(),
  dateTime: z.string(),
  description: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string().optional(),
    additionalImageUrls: z.array(z.string()).optional(),
  additionalFileUrls: z.array(z.string()).optional()
})

// Event Filter Params Schema
export const EventFilterParamsSchema = z.object({
  upcoming: z.boolean().optional(),
  year: z.string().optional(),
  month: z.string().optional()
})

// Event type exports
export type Event = z.infer<typeof EventSchema>
export type EventDetail = z.infer<typeof EventDetailSchema>
export type EventFilterParams = z.infer<typeof EventFilterParamsSchema>

// Event Comment Schema
export const EventCommentSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  eventTitle: z.string().optional(),
  name: z.string(),
  email: z.string(),
  comment: z.string(),
  dateCommented: z.string(),
  replyCount: z.number().optional().default(0)
})

// Event Reply Schema
export const EventReplySchema = z.object({
  id: z.string(),
  eventId: z.string(),
  eventTitle: z.string().optional(),
  responderName: z.string(),
  responderEmail: z.string(),
  response: z.string(),
  dateReplied: z.string()
})

// Event Comment with Replies Schema
export const EventCommentWithRepliesSchema = z.object({
  eventCommentResponse: EventCommentSchema,
  eventReplyResponses: z.array(EventReplySchema).default([])
})

// Event Comment Create Schema
export const EventCommentCreateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Valid email is required'),
  comment: z.string().min(1, 'Comment is required')
})

// Event Reply Create Schema
export const EventReplyCreateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Valid email is required'),
  reply: z.string().min(1, 'Reply is required')
})

// Event Comment/Reply type exports
export type EventComment = z.infer<typeof EventCommentSchema>
export type EventReply = z.infer<typeof EventReplySchema>
export type EventCommentWithReplies = z.infer<typeof EventCommentWithRepliesSchema>
export type EventCommentCreate = z.infer<typeof EventCommentCreateSchema>
export type EventReplyCreate = z.infer<typeof EventReplyCreateSchema>

// Tender Schema (for list/filter responses)
export const TenderSchema = z.object({
  tenderId: z.string(),
  tenderTitle: z.string(),
  bidType: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  description: z.string(),
  tenderUrl: z.string().nullable(),
  datePublished: z.string()
})

// Tender Detail Schema (for get by ID responses)
export const TenderDetailSchema = z.object({
  tenderId: z.string(),
  tenderTitle: z.string(),
  bidType: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  description: z.string(),
  tenderUrl: z.string().nullable(),
  datePublished: z.string()
})

// Tender Filter Params Schema
export const TenderFilterParamsSchema = z.object({
  tenderCategory: z.string().optional(),
  bidType: z.string().optional(),
  status: z.string().optional(),
  year: z.string().optional(),
  month: z.string().optional(),
  endingSoon: z.boolean().optional(),
  latest: z.boolean().optional() 
})

// Tender type exports
export type Tender = z.infer<typeof TenderSchema>
export type TenderDetail = z.infer<typeof TenderDetailSchema>
export type TenderFilterParams = z.infer<typeof TenderFilterParamsSchema>

// Video Schema (for list/filter responses)
export const VideoSchema = z.object({
  videoUploadId: z.string(),
  title: z.string(),
  description: z.string(),
  embeddedLink: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  datePublished: z.string().nullable(),
  createdAt: z.string()
})

// Video Detail Schema (for get by ID responses)
export const VideoDetailSchema = z.object({
  videoUploadId: z.string(),
  title: z.string(),
  description: z.string(),
  embeddedLink: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  datePublished: z.string().nullable(),
  createdAt: z.string()
})

// Video Filter Params Schema
export const VideoFilterParamsSchema = z.object({
  key: z.string().optional(),
  folderId: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  isKeyHighlight: z.union([z.boolean(), z.string()]).optional(),
  isMultimedia: z.union([z.boolean(), z.string()]).optional()
})

// Video Folder Search Result Schema
export const VideoFolderSearchResultSchema = z.object({
  folderId: z.string(),
  folderName: z.string(),
  videos: z.array(VideoSchema).default([]),
  previewUrls: z.array(z.string()).optional()
})

// Video type exports
export type Video = z.infer<typeof VideoSchema>
export type VideoDetail = z.infer<typeof VideoDetailSchema>
export type VideoFilterParams = z.infer<typeof VideoFilterParamsSchema>
export type VideoFolderSearchResult = z.infer<typeof VideoFolderSearchResultSchema>

// Newsletter Subscription Schema
export const NewsletterSubscriptionSchema = z.object({
  subscriberId: z.string(),
  email: z.string().email(),
  dateJoined: z.string()
})

// Newsletter Subscribe Request Schema
export const NewsletterSubscribeRequestSchema = z.object({
  email: z.string().email()
})

// Newsletter type exports
export type NewsletterSubscription = z.infer<typeof NewsletterSubscriptionSchema>
export type NewsletterSubscribeRequest = z.infer<typeof NewsletterSubscribeRequestSchema>

// Country Schema (for individual country responses from /countries/public/:id)
export const CountrySchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  countryTag: z.string(),
  regionTag: z.string(),
  countryName: z.string(),
  countryDescription: z.string(),
  flagUrl: z.string(),
  abbreviation: z.string()
})

// Country type exports
export type Country = z.infer<typeof CountrySchema>

